/**
 * InstagramChatBanner — compact Instagram chat reference bar with modal dialog
 * for full chat conversation preview (bubbles, speaker names, and message count).
 */
import { useMemo, useState, type JSX } from 'react'
import { Dialog } from 'primereact/dialog'
import { Eye, MessageCircle } from 'lucide-react'
import { InstagramIcon } from '../../modules/conversations/BrandIcons.js'
import { cleanIdentifier, isNumericOrRawId } from '../../utils/formatCustomerName.js'
import { parseTranscript, type TranscriptLine } from '../conversations/detail/DescriptionTranscript.js'

export interface InstagramChatBannerMessage {
  id?: string
  senderName?: string | null
  senderHandle?: string | null
  contentText?: string | null
  direction?: string
  sentAt?: string | Date | null
}

export interface InstagramChatBannerProps {
  /** Account or customer name (e.g. displayName or Instagram username) */
  accountHolderName?: string | null
  /** Instagram user ID or account ID (e.g. primaryHandle) */
  accountId?: string | null
  /** Raw conversation description containing chat transcript */
  description?: string | null
  /** Message list if available */
  messages?: InstagramChatBannerMessage[] | null
  /** Compact variant for dense message lists */
  compact?: boolean
  /** Button label (defaults to 'View') */
  buttonLabel?: string
}

export function InstagramChatBanner({
  accountHolderName,
  accountId,
  description,
  messages,
  compact = false,
  buttonLabel = 'View',
}: InstagramChatBannerProps): JSX.Element | null {
  const [dialogVisible, setDialogVisible] = useState(false)

  const rawAccountName = accountHolderName?.trim() ?? ''
  const displayAccountName = useMemo(() => {
    const cleaned = cleanIdentifier(rawAccountName)
    if (cleaned && !isNumericOrRawId(cleaned)) {
      return cleaned
    }
    return 'Instagram User'
  }, [rawAccountName])

  const displayId = useMemo(() => {
    const raw = (accountId?.trim() || cleanIdentifier(rawAccountName) || '').trim()
    return raw
  }, [accountId, rawAccountName])

  // Parse lines from description or messages
  const transcriptLines = useMemo<TranscriptLine[]>(() => {
    if (description?.trim()) {
      const parsed = parseTranscript(description, displayAccountName, { minLines: 1 })
      if (parsed && parsed.lines.length > 0) {
        return parsed.lines
      }
    }

    if (messages && messages.length > 0) {
      return messages
        .filter((m) => Boolean(m.contentText?.trim()))
        .map((m) => {
          const isOutbound = m.direction === 'outbound'
          const speaker = m.senderName?.trim() || (isOutbound ? 'Agent' : displayAccountName)
          return {
            speaker,
            text: m.contentText?.trim() ?? '',
            outbound: isOutbound,
          }
        })
    }

    if (description?.trim()) {
      return [
        {
          speaker: displayAccountName,
          text: description.trim(),
          outbound: false,
        },
      ]
    }

    return []
  }, [description, messages, displayAccountName])

  return (
    <>
      {/* Compact Instagram Chat Reference Bar */}
      <section
        data-testid="instagram-chat-banner"
        className={[
          'flex min-w-0 items-center justify-between gap-3 border border-[#C13584]/20 bg-[#C13584]/5 transition-colors',
          compact
            ? 'mb-2.5 rounded-[3px] px-2.5 py-1.5'
            : 'shrink-0 rounded-[3px] px-3.5 py-2',
        ].join(' ')}
        aria-label="Instagram chat reference"
      >
        <div className="flex min-w-0 items-center gap-2.5">
          <span
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded-[3px] bg-[#C13584] text-white shadow-xs"
            aria-hidden="true"
          >
            <InstagramIcon className="h-3 w-3" />
          </span>

          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate text-xs font-semibold text-slate-800">
              {displayAccountName}
            </span>

            {displayId && (
              <>
                <span className="text-slate-300" aria-hidden="true">·</span>
                <span
                  className="truncate font-mono text-[11px] text-slate-500"
                  title={displayId}
                >
                  {displayId.startsWith('@') ? displayId : `ID: ${displayId}`}
                </span>
              </>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={() => setDialogVisible(true)}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-[3px] bg-[#C13584] px-2.5 py-1 text-xs font-medium text-white shadow-xs transition-colors hover:bg-[#b02e77] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C13584]"
          aria-label="View Instagram Chat"
        >
          <Eye className="h-3.5 w-3.5" aria-hidden="true" />
          <span>{buttonLabel}</span>
        </button>
      </section>

      {/* Instagram Chat Modal Dialog */}
      <Dialog
        header={
          <div className="flex items-center gap-2">
            <span
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[3px] bg-[#C13584] text-white"
              aria-hidden="true"
            >
              <InstagramIcon className="h-3.5 w-3.5" />
            </span>
            <div className="min-w-0">
              <span className="text-sm font-bold text-slate-900">
                Instagram Chat
              </span>
              {displayAccountName !== 'Instagram User' && (
                <span className="ml-1.5 text-xs font-normal text-slate-500">
                  with {displayAccountName}
                </span>
              )}
            </div>
          </div>
        }
        visible={dialogVisible}
        onHide={() => setDialogVisible(false)}
        style={{ width: '600px', maxWidth: '95vw' }}
        modal
        dismissableMask
        className="p-fluid"
      >
        <div className="max-h-[75vh] space-y-4 overflow-y-auto pr-1">
          {/* Metadata header strip */}
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-[3px] border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
            <div className="min-w-0 space-y-0.5">
              <p className="font-semibold text-slate-800">
                User: <span className="font-medium text-slate-700">{displayAccountName}</span>
              </p>
              {displayId && (
                <p className="truncate font-mono text-[11px] text-slate-500" title={displayId}>
                  Handle / ID: {displayId}
                </p>
              )}
            </div>

            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <MessageCircle className="h-3.5 w-3.5 text-[#C13584]" />
              <span>{`${transcriptLines.length} ${transcriptLines.length === 1 ? 'message' : 'messages'}`}</span>
            </div>
          </div>

          {/* Chat bubbles */}
          {transcriptLines.length > 0 ? (
            <div className="space-y-2 rounded-[3px] border border-slate-200 bg-slate-50 p-3.5">
              {transcriptLines.map((line, idx) => (
                <div
                  key={idx}
                  className={`flex flex-col ${line.outbound ? 'items-end' : 'items-start'}`}
                >
                  <span
                    className={`mb-0.5 text-[10px] font-medium text-slate-400 ${
                      line.outbound ? 'pr-1 text-right' : 'pl-1'
                    }`}
                  >
                    {line.speaker}
                  </span>
                  <div
                    className={`max-w-[80%] rounded-[12px] px-3.5 py-2 text-sm shadow-xs ${
                      line.outbound
                        ? 'bg-[#C13584] text-white'
                        : 'border border-slate-200 bg-white text-slate-800'
                    }`}
                  >
                    <p className="whitespace-pre-wrap break-words leading-relaxed">{line.text || '—'}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-[3px] border border-slate-100 bg-slate-50 p-4 text-center text-xs italic text-slate-500">
              No chat messages available in this conversation.
            </div>
          )}
        </div>

        {/* Modal footer */}
        <div className="mt-4 flex items-center justify-end gap-2 border-t border-slate-100 pt-3">
          <button
            type="button"
            onClick={() => setDialogVisible(false)}
            className="rounded-[3px] bg-slate-100 px-3.5 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-200 focus-visible:outline-none"
          >
            Close
          </button>
        </div>
      </Dialog>
    </>
  )
}

export default InstagramChatBanner
